import Card from "@/components/card"
import { PhysicalRisk, RiskType } from "@/entities/physical-risk/model/physical-risk.types"
import { PhysicalRiskMeter } from "@/entities/physical-risk/ui/PhysicalRiskMeter"
import RiskIcon from "@/entities/physical-risk/ui/RiskIcon"
import { faBuilding } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import RpsLevel from "@/entities/population/ui/RpsLevel"
import PhysicalRisksIdentifiedWidget, {
  RiskDistributionItem
} from "@/entities/physical-risk/ui/PhysicalRisksIdentifiedWidget"

interface RiskSummary {
  title: string
  type: RiskType
  risk?: PhysicalRisk | null
  //RPS : niveau de la situation la plus elevee et sa fonction, a la place d'un risque
  rpsLevel?: { level: number; populationName: string } | null
  onRiskClick?: () => void
  establishmentName?: string
  onEstablishmentClick?: () => void
}

interface Props {
  riskDistribution: RiskDistributionItem[]
  phy: RiskSummary
  rps: RiskSummary
}

function getRiskName(risk?: PhysicalRisk | null) {
  if (!risk) return null
  return risk.cName || Object.values(risk.attributes ?? {}).find((v) => v) || "Risque sans nom"
}

function SectionTitle({ children, prefix }: { children: React.ReactNode; prefix: React.ReactNode }) {
  return (
    <div className="flex h-[22px] items-start justify-center">
      <div className="flex items-center justify-center font-normal leading-tight">
        {prefix}
        &nbsp;
        <span>{children}</span>
      </div>
    </div>
  )
}

function RiskColumn({ summary }: { summary: RiskSummary }) {
  const riskName = summary.rpsLevel ? summary.rpsLevel.populationName : getRiskName(summary.risk)

  return (
    <div className="flex h-[130px] min-w-0 flex-1 flex-col items-center text-center">
      <SectionTitle prefix={<RiskIcon riskType={summary.type} />}>{summary.title}</SectionTitle>

      {summary.risk || summary.rpsLevel ? (
        <>
          <div className="flex w-full min-w-0 flex-1 flex-col items-center justify-center pt-2">
            <div
              className={summary.onRiskClick ? "_hover cursor-pointer" : ""}
              onClick={(e) => {
                e.stopPropagation()
                summary.onRiskClick?.()
              }}
              title={summary.onRiskClick ? "Ouvrir la fiche du risque" : undefined}>
              {summary.rpsLevel ? (
                <RpsLevel level={summary.rpsLevel.level} isLabelVisible />
              ) : (
                <PhysicalRiskMeter risk={summary.risk ?? null} size="sm" />
              )}
            </div>

            <div className="mt-3 w-full min-w-0">
              <b
                className={`grey-t block h-[20px] w-full min-w-0 truncate text-sm ${summary.onRiskClick ? "cursor-pointer hover:underline" : ""}`}
                onClick={(e) => {
                  e.stopPropagation()
                  summary.onRiskClick?.()
                }}>
                {riskName}
              </b>
            </div>
          </div>

          {summary.establishmentName ? (
            <div
              className={`grey-t flex h-[20px] max-w-full items-center text-sm ${summary.onEstablishmentClick ? "cursor-pointer hover:underline" : ""}`}
              onClick={(e) => {
                e.stopPropagation()
                summary.onEstablishmentClick?.()
              }}>
              <FontAwesomeIcon icon={faBuilding} className="shrink-0 mr-[6px]" />
              <span className="min-w-0 truncate">{summary.establishmentName}</span>
            </div>
          ) : (
            <div className="h-[20px]" />
          )}
        </>
      ) : (
        <>
          <div className="grey-t flex flex-1 items-center text-sm">Aucun risque</div>
          <div className="h-[20px]" />
        </>
      )}
    </div>
  )
}

export default function PhysicalRisksDashboardCard({ phy, riskDistribution, rps }: Props) {
  return (
    <Card isWithoutPadding className="physical-risks-dashboard-card flex h-[190px] w-full items-center justify-center">
      <div className="flex w-full min-h-0 items-stretch gap-10">
        <PhysicalRisksIdentifiedWidget riskDistribution={riskDistribution} />
        <RiskColumn summary={phy} />
        <RiskColumn summary={rps} />
      </div>
    </Card>
  )
}
